import React from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

interface SentryConfig {
  dsn: string;
  environment: string;
  debug?: boolean;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

/**
 * Initialize Sentry with proper configuration
 */
export function initSentry() {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_NODE_ENV || "development";
  const isProduction = environment === "production";
  const isDevelopment = !isProduction;

  if (!sentryDsn) {
    console.warn("No Sentry DSN available. Error tracking will not work.");
    return;
  }

  const config: SentryConfig = {
    dsn: sentryDsn,
    environment,
    debug: false, // Set to false to reduce console noise
    // Performance monitoring
    tracesSampleRate: isProduction ? 0.01 : 1.0, // 1% for production, 100% for development
    // Session replay
    replaysSessionSampleRate: isProduction ? 0.01 : 1.0, // 1% for production, 100% for development
    replaysOnErrorSampleRate: 1.0,
  };

  Sentry.init({
    ...config,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: false,
    // Configure trace propagation for distributed tracing
    tracePropagationTargets: [
      "localhost",
      /^\/api\//,
      /^\/trpc\//,
      "http://localhost:3000",
      "https://api.samphireneuro.com",
      "https://preview-api.samphireneuro.com",
    ],
    // Add allowed URLs for CORS
    allowUrls: [
      "http://localhost:5173",
      "https://localhost:5173",
      "https://web.samphireneuro.com",
      "https://preview-app.samphireneuro.com",
    ],
    integrations: [
      // Router integration for better error context
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay integration for debugging
      Sentry.replayIntegration({
        // Capture console logs
        maskAllText: isProduction,
        blockAllMedia: isProduction,
      }),
    ],
    beforeSend(event) {
      // Log all events in development for debugging
      if (isDevelopment) {
        console.log("🔍 Sentry event being sent:", event);
      }

      return event;
    },
  });

  // Set initial context with safe version information
  Sentry.setContext("app", {
    name: "Samphire Web",
    version: import.meta.env.VITE_APP_VERSION || "unknown",
    buildTime: import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
    environment: environment,
  });
}

/**
 * Capture an error with additional context
 */
export function captureError(error: Error, context?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a message with level
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>,
) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
    }
    Sentry.captureMessage(message, level);
  });
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  // Only include non-PII data unless explicitly configured
  const userData: any = {
    id: user.id,
  };

  Sentry.setUser(userData);
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, any>,
) {
  Sentry.addBreadcrumb({
    message,
    category: category || "custom",
    data,
    level: "info",
  });
}

// Re-export Sentry for direct use when needed
export { Sentry };
